import React, { forwardRef } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import sv from 'date-fns/locale/sv'
import format from 'date-fns/format'
import getMonth from 'date-fns/getMonth'
import getYear from 'date-fns/getYear'
import isValid from 'date-fns/isValid'
import parse from 'date-fns/parse'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import { useMediaQuery } from 'react-responsive'
import theme from '@app/ui/theme'
import * as S from './DateField.styles'

registerLocale('sv', sv)

const DATE_FORMAT = 'yyyy-MM-dd'

const MONTHS = Array.from({ length: 12 }, (_, month) => {
  const name = sv.localize?.month(month, { width: 'wide' }) ?? ''
  return name.charAt(0).toUpperCase() + name.slice(1)
})

const CURRENT_YEAR = new Date().getFullYear()
const YEARS = Array.from(
  { length: 36 },
  (_, index) => CURRENT_YEAR - 30 + index
)

// Older tables were saved before the form settled on a plain date, so a full
// timestamp still has to open on the right day.
const parseValue = (value?: string): Date | null => {
  if (!value) return null
  const exact = parse(value, DATE_FORMAT, new Date())
  if (isValid(exact)) return exact
  const loose = new Date(value)
  return isValid(loose) ? loose : null
}

type TriggerProps = React.InputHTMLAttributes<HTMLInputElement> & {
  hasError?: boolean
}

const DateTrigger = forwardRef<HTMLInputElement, TriggerProps>(
  ({ hasError, ...inputProps }, ref) => (
    <S.TriggerWrapper>
      <S.TriggerInput {...inputProps} ref={ref} hasError={hasError} />
      <S.TriggerIcon>
        <FeatherIcon
          name="calendar"
          size={18}
          color={hasError ? theme.colors.warning : theme.colors.secondary}
        />
      </S.TriggerIcon>
    </S.TriggerWrapper>
  )
)

DateTrigger.displayName = 'DateTrigger'

type Props = {
  value: string
  onChange: (value: string) => void
  hasError?: boolean
  placeholder?: string
  disabled?: boolean
}

const DateField: React.FC<Props> = ({
  value,
  onChange,
  hasError,
  placeholder = 'Select a date',
  disabled,
}) => {
  const isPhone = useMediaQuery({ query: '(max-width: 576px)' })

  return (
    <S.FieldWrapper>
      <S.CalendarStyles />
      <DatePicker
        selected={parseValue(value)}
        onChange={(date: Date | null) =>
          onChange(date ? format(date, DATE_FORMAT) : '')
        }
        dateFormat={DATE_FORMAT}
        locale="sv"
        placeholderText={placeholder}
        disabled={disabled}
        customInput={<DateTrigger hasError={hasError} />}
        calendarClassName="mi-datepicker"
        popperClassName="mi-datepicker-popper"
        showPopperArrow={false}
        fixedHeight
        todayButton="Today"
        // A phone gets the centred overlay, everything else a popper that is
        // kept inside the viewport and flips above the field when it has to.
        withPortal={isPhone}
        portalId="datepicker-root"
        popperPlacement="bottom-start"
        popperProps={{ strategy: 'fixed' }}
        popperModifiers={[
          { name: 'offset', options: { offset: [0, 0] } },
          {
            name: 'preventOverflow',
            options: {
              padding: 12,
              altAxis: true,
              tether: false,
              rootBoundary: 'viewport',
            },
          },
          {
            name: 'flip',
            options: {
              padding: 12,
              rootBoundary: 'viewport',
              fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
            },
          },
        ]}
        renderCustomHeader={({
          date,
          changeMonth,
          changeYear,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <S.CalendarHeader>
            <S.NavButton
              type="button"
              aria-label="Previous month"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
            >
              <FeatherIcon name="chevron-left" size={18} />
            </S.NavButton>
            <S.HeaderSelects>
              <S.HeaderSelect
                aria-label="Month"
                value={getMonth(date)}
                onChange={({ target }) => changeMonth(+target.value)}
              >
                {MONTHS.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </S.HeaderSelect>
              <S.HeaderSelect
                aria-label="Year"
                value={getYear(date)}
                onChange={({ target }) => changeYear(+target.value)}
              >
                {YEARS.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </S.HeaderSelect>
            </S.HeaderSelects>
            <S.NavButton
              type="button"
              aria-label="Next month"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
            >
              <FeatherIcon name="chevron-right" size={18} />
            </S.NavButton>
          </S.CalendarHeader>
        )}
      />
    </S.FieldWrapper>
  )
}

export default DateField
